import { PRODUCT_CATEGORY_GROUPS } from './productNavigation';

const WORD_BOUNDARY_PHRASES = [
  'Hippo',
  'South Africa',
  'SA',
  'RSA',
  'ZA',
  'OTP',
  'SMS',
  'FAQ',
  'FAQs',
  'CTA',
  'API',
  'GPS',
  'MUI',
];

// Product names the content team title-cases wherever they appear, even
// mid-sentence ("Funeral Cover for parents"). PRODUCT_CATEGORY_GROUPS covers
// the ones with their own nav entry; these are the rest. Add to this list when
// a new product name starts showing up in page headings — it is the only part
// of the casing rules that needs occasional upkeep.
const PRODUCT_NAME_PHRASES = [
  'Chronic Condition Cover',
  'Commercial Property Insurance',
  'Credit Risk Insurance',
  'Critical Illness Cover',
  'Directors Insurance',
  "Employers' Liability Insurance",
  'Family Funeral Cover',
  'Funeral Cover',
  'Hospital Plans',
  'Key Person Insurance',
  'Product Liability Insurance',
  'Professional Indemnity Insurance',
  'Public Liability Insurance',
  'Special Home Insurance',
  'Whole Life Insurance',
];

const PRODUCT_PHRASES = PRODUCT_CATEGORY_GROUPS.flatMap(
  ({ title, links = [], launchPadOptions = [] }) => [
    title,
    ...links.map(({ label }) => label),
    ...launchPadOptions,
  ]
);

const KNOWN_PHRASES = Array.from(
  new Set([
    ...WORD_BOUNDARY_PHRASES,
    ...PRODUCT_NAME_PHRASES,
    ...PRODUCT_PHRASES.filter((phrase) => /[\s&/-]/.test(phrase)),
  ])
).filter(Boolean);

const PRESERVED_TITLE_CASE_WORDS = new Map(
  WORD_BOUNDARY_PHRASES.filter((phrase) => !phrase.includes(' ')).map(
    (phrase) => [phrase.toLowerCase(), phrase]
  )
);

const normalizeWhitespace = (value) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isAllCapsAcronym = (value) =>
  /^[A-Z0-9&.-]+$/.test(value) &&
  value === value.toUpperCase() &&
  value.length <= 6;

const titleCaseWord = (word) => {
  if (!word) return word;
  if (isAllCapsAcronym(word)) return word;

  const preservedWord = PRESERVED_TITLE_CASE_WORDS.get(word.toLowerCase());
  if (preservedWord) return preservedWord;

  return word
    .toLowerCase()
    .replace(
      /(^|[-/(\s])([a-z])/g,
      (match, prefix, char) => `${prefix}${char.toUpperCase()}`
    )
    .replace(/^([a-z])/, (match) => match.toUpperCase());
};

export const toTitleCase = (value = '') => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';

  return normalized
    .split(/(\s+)/)
    .map((segment) => {
      if (!segment.trim()) return segment;
      if (segment === '&') return segment;
      return segment
        .split('-')
        .map((part) => titleCaseWord(part))
        .join('-');
    })
    .join('');
};

const restoreKnownPhrases = (text) => {
  let next = text;
  const phrases = [...KNOWN_PHRASES].sort((a, b) => b.length - a.length);

  for (const phrase of phrases) {
    const pattern = new RegExp(
      `(^|[^A-Za-z0-9])(${escapeRegex(phrase.toLowerCase())})(?=$|[^A-Za-z0-9])`,
      'gi'
    );
    next = next.replace(pattern, (match, prefix) => `${prefix}${phrase}`);
  }

  return next;
};

const restoreCurrencyPrefixes = (text) => text.replace(/\br(?=\s*\d)/g, 'R');

const restorePronouns = (text) =>
  text
    .replace(/\bi(['’](?:m|d|ll|ve|re|s|t))\b/g, 'I$1')
    .replace(/\bi\b(?!['’]|\.[a-z])/g, 'I');

// A word is "ordinary" — safe to lowercase mid-sentence — when it is all
// lowercase or plain Titlecase. Anything else carries deliberate casing that
// only a person could have authored: an acronym (BMW, GWM), an internal
// capital (McLaren, MiWay, EcoSport, iX), a letter/digit mix (RAV4, NP200,
// 1Life) or a cased hyphenation (T-Roc, Mercedes-Benz). Those are proper nouns
// often enough that preserving them beats lowercasing them, and it means new
// brands, partners and car models come out right with no list to maintain.
const isOrdinaryWord = (word) => {
  // Apostrophes are part of ordinary words ("Africa's", "Employers'"), not a
  // sign of deliberate casing. Leaving them in would mask the word, and a
  // masked word breaks any known phrase spanning it — "South Africa's" would
  // come out as "south Africa's".
  const bare = word.replace(/['’]/g, '');
  if (!bare) return true;
  if (bare === bare.toLowerCase()) return true;
  return /^[A-Z][a-z]*$/.test(bare);
};

// Text typed entirely in capitals is shouting, not a run of proper nouns, so
// it still gets sentence-cased ("SUZUKI SWIFT 1.2 GLX" -> "Suzuki swift 1.2
// glx"). A single all-caps word is left to the rule above, where it reads as
// an acronym or badge — "BMW", "RAV4", "MINI".
const isAllCaps = (value) => {
  const words = value.split(/\s+/).filter(Boolean);
  return words.length > 1 && !/\p{Ll}/u.test(value) && /\p{Lu}/u.test(value);
};

// A word: letters and digits, plus the apostrophes, hyphens and ampersands
// that sit inside one ("Africa's", "Mercedes-Benz", "B&B"). Matching words directly rather than splitting on whitespace keeps
// surrounding punctuation and markup out of the comparison, so "(ISPs)." keeps
// its sentence-ending period and "<p>Business" is still seen as the word
// "Business".
const WORD_PATTERN = /[\p{L}\p{N}][\p{L}\p{N}'’&-]*/gu;

// Placeholder that survives lowercasing and the known-phrase pass unchanged.
const maskToken = (index) => `qqmask${index}qq`;
const MASK_PATTERN = /qqmask(\d+)qq/gi;

export const toSentenceCase = (value = '') => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';

  // Set aside the words whose casing must not be touched, so the sentence pass
  // below can lowercase freely without flattening proper nouns.
  const preserved = [];
  const masked = isAllCaps(normalized)
    ? normalized
    : normalized.replace(WORD_PATTERN, (word) => {
        if (isOrdinaryWord(word)) return word;
        preserved.push(word);
        return maskToken(preserved.length - 1);
      });

  const lowerCased = masked.toLowerCase();
  const sentenceCased = lowerCased.replace(
    /(^|[.!?]\s+)([a-z])/g,
    (match, prefix, char) => `${prefix}${char.toUpperCase()}`
  );

  const restored = restorePronouns(
    restoreCurrencyPrefixes(restoreKnownPhrases(sentenceCased))
  );

  return restored.replace(MASK_PATTERN, (match, index) => preserved[+index]);
};
